/**
 * خواندن متن با صدای مرورگر (Web Speech Synthesis).
 *
 * چرا کتابخانه لازم نیست: این API در خود مرورگر هست، هیچ درخواست
 * شبکه‌ای ندارد و هیچ حجمی به برنامه اضافه نمی‌کند.
 *
 * مهم‌ترین محدودیت، صادقانه: صدای فارسی روی همه‌ی دستگاه‌ها نیست.
 * اندروید معمولاً دارد، iOS باید نصب شود، ویندوز معمولاً ندارد و
 * لینوکس تقریباً هیچ‌وقت. خواندن فارسی با صدای انگلیسی بدتر از
 * سکوت است — پس اگر صدای فارسی نبود، اصلاً نمی‌خوانیم.
 */

import { toEnglishDigits } from "./text";
import { fetchSpeech, isCloudTtsEnabled, speechUrl } from "./llm";
import {
    isReady as isPiperReady,
    speakWithPiper,
    stop as piperStop,
} from "./piperVoice";

const KEY = "havir-assistant-speak";

let enabled = false;
let cachedVoice: SpeechSynthesisVoice | null = null;

export const isSpeechSupported = (): boolean =>
    typeof window !== "undefined" && "speechSynthesis" in window;

export const loadSpeakPreference = (): boolean => {
    try {
        enabled = localStorage.getItem(KEY) === "1";
    } catch {
        enabled = false;
    }
    return enabled;
};

export const setSpeakEnabled = (value: boolean): void => {
    enabled = !!value;
    try {
        localStorage.setItem(KEY, enabled ? "1" : "0");
    } catch {
        /* حالت خصوصی مرورگر — نادیده */
    }
    if (!enabled) stopSpeaking();
};

export const isSpeakEnabled = (): boolean => enabled;

/*
  صداها همگام بارگذاری نمی‌شوند. اولین بار getVoices() آرایه‌ی خالی
  برمی‌گرداند و بعد رویداد voiceschanged می‌آید. بدون این، اولین
  جمله با صدای پیش‌فرض انگلیسی خوانده می‌شود.
*/
const pickVoice = (): SpeechSynthesisVoice | null => {
    if (!isSpeechSupported()) return null;
    if (cachedVoice) return cachedVoice;

    const voices = window.speechSynthesis.getVoices();
    if (!voices.length) return null;

    const persianVoices = voices.filter((voice) => {
        const lang = String(voice.lang || "").toLowerCase().replace(/_/g, "-");
        return lang === "fa-ir" || lang === "fa" || /persian|farsi|iran/i.test(voice.name || "");
    });
    const score = (voice: SpeechSynthesisVoice): number => {
        const lang = String(voice.lang || "").toLowerCase().replace(/_/g, "-");
        let value = lang === "fa-ir" ? 4 : lang === "fa" ? 3 : 1;
        // مرورگرها گاهی صدای آنلاینِ طبیعی را کنار صدای محلی فهرست می‌کنند.
        if (voice.localService === false) value += 2;
        if (/natural|neural|online|premium/i.test(voice.name || "")) value += 3;
        return value;
    };

    cachedVoice = persianVoices.sort((a, b) => score(b) - score(a))[0] || null;
    return cachedVoice;
};

/**
 * وضعیت صدا، برای اینکه بتوانیم به کاربر *بگوییم* چرا ساکت است.
 *
 * سکوت بی‌توضیح بدترین حالت است: کاربر فکر می‌کند قابلیت خراب است،
 * در حالی که فقط صدای فارسی روی دستگاهش نصب نیست.
 */
export const voiceStatus = (): { persian: boolean; name: string; total: number } => {
    if (!isSpeechSupported()) return { persian: false, name: "", total: 0 };

    const voices = window.speechSynthesis.getVoices();
    const chosen = pickVoice();
    return {
        persian: !!chosen,
        name: chosen ? chosen.name : "",
        total: voices.length,
    };
};

if (typeof window !== "undefined" && window.speechSynthesis) {
    window.speechSynthesis.onvoiceschanged = () => {
        cachedVoice = null;
        pickVoice();
    };
}

export const hasPersianVoice = (): boolean => {
    if (!isSpeechSupported()) return false;
    return !!pickVoice();
};

/*
  متن پاسخ برای چشم نوشته شده، نه گوش: علامت، بولت، عدد جدولی و
  آدرس دارد. خوانده شدن این‌ها آزاردهنده است.
*/
/** نام ماه‌های شمسی، برای خواندن تاریخ به زبان آدم. */
const JALALI_MONTHS = [
    "فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور",
    "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند",
];

/*
  تاریخ و ساعت را برای گوش آماده می‌کند.

  «۱۴۰۵/۰۶/۳۰» را موتور صدا رقم‌به‌رقم یا با «اسلش» می‌خواند و نتیجه
  نامفهوم است. تبدیلش می‌کنیم به «۳۰ شهریور ۱۴۰۵».

  ساعت هم کلاً حذف می‌شود: در جواب‌های این سامانه «۱۴:۲۳:۱۱» هیچ
  اطلاعاتی به شنونده نمی‌دهد و فقط جمله را شلوغ می‌کند.
*/
const datesForSpeech = (text: string): string =>
    toEnglishDigits(text)
        // اول ساعت، بعد تاریخ: اگر برعکس باشد، «۳۰ ۱۴:۲۳» باقی‌مانده
        // به تاریخ دیگری چسبیده می‌شود.
        .replace(/\s*\d{1,2}:\d{2}(:\d{2})?\s*/g, " ")
        .replace(
            /(1[34]\d{2})\s*[/\-.]\s*(\d{1,2})\s*[/\-.]\s*(\d{1,2})/g,
            (whole, year: string, month: string, day: string) => {
                const index = Number(month) - 1;
                if (index < 0 || index > 11) return whole;
                return Number(day) + " " + JALALI_MONTHS[index] + " " + Number(year);
            }
        );

const forSpeech = (text: unknown): string =>
    datesForSpeech(String(text || ""))
        // موتورهای فارسی بعضی وقت‌ها اعشار را رقم‌به‌رقم یا با مکث
        // نامناسب می‌خوانند؛ «۰.۹» را به «صفر ممیز نه» تبدیل می‌کنیم.
        .replace(/[0-9۰-۹٠-٩]+[.,٫][0-9۰-۹٠-٩]+/g, (value) => {
            const parts = toEnglishDigits(value).replace(",", ".").replace("٫", ".").split(".");
            const smallNumbers = [
                "صفر", "یک", "دو", "سه", "چهار", "پنج", "شش", "هفت", "هشت", "نه",
                "ده", "یازده", "دوازده", "سیزده", "چهارده", "پانزده", "شانزده", "هفده", "هجده", "نوزده",
            ];
            const tens = ["", "", "بیست", "سی", "چهل", "پنجاه", "شصت", "هفتاد", "هشتاد", "نود"];
            const hundreds = ["", "صد", "دویست", "سیصد", "چهارصد", "پانصد", "ششصد", "هفتصد", "هشتصد", "نهصد"];
            const words = (raw: string): string => {
                const number = Number(raw);
                if (!number) return "صفر";
                if (number < 20) return smallNumbers[number];
                if (number < 100) {
                    const remainder = number % 10;
                    return tens[Math.floor(number / 10)] + (remainder ? " و " + smallNumbers[remainder] : "");
                }
                if (number < 1000) {
                    const remainder = number % 100;
                    return hundreds[Math.floor(number / 100)] + (remainder ? " و " + words(String(remainder)) : "");
                }
                return raw;
            };

            if (!parts[1] || /^0+$/.test(parts[1])) return words(parts[0]);
            const fraction = parts[1].charAt(0) === "0"
                ? parts[1].split("").map((digit) => smallNumbers[Number(digit)]).join(" ")
                : words(parts[1]);
            return words(parts[0]) + " ممیز " + fraction;
        })
        .replace(/<[^>]*>/g, " ")
        .replace(/https?:\/\/\S+/g, " ")
        // بدون پرچم u نوشته شده: هدف کامپایل es5 است و ایموجی‌ها با
        // جفت جانشین (surrogate pair) حذف می‌شوند، نه با \p{...}
        .replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, " ")
        .replace(/[☀-➿️⬀-⯿]/g, " ")
        .replace(/^[•\-*]\s*/gm, "")
        // شکست خط معمولاً مرز جمله یا ردیف است؛ آن را به مکث کوتاه
        // تبدیل می‌کنیم تا پاسخ‌های چندخطی یک‌نفس و یکنواخت خوانده نشوند.
        .replace(/[.!؟]\s*\n+/g, ". ")
        .replace(/\n+/g, ". ")
        .replace(/[«»"|]/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 600); // جواب خیلی بلند را کسی تا آخر گوش نمی‌دهد

/*
  در گفتگوی پیوسته، میکروفون باید دقیقاً وقتی باز شود که دستیار
  حرفش تمام شده — وگرنه صدای بلندگو وارد میکروفون می‌شود و دستیار
  حرف خودش را می‌شنود. پس حالت «در حال خواندن» باید از بیرون قابل
  دیدن باشد.

  onend همیشه نمی‌آید (کاربر cancel می‌زند، تب پنهان می‌شود، صدا
  نیمه‌کاره می‌ماند). برای همین یک مهلت پشتیبان هم هست، وگرنه
  میکروفون برای همیشه بسته می‌ماند.
*/
let speaking = false;
let doneCallback: (() => void) | null = null;
let fallbackTimer: any = null;

/** سقف انتظار برای onend — حدوداً ۱۸۰ کلمه در دقیقه، با حاشیه. */
const speechTimeoutFor = (text: string): number =>
    Math.min(60000, 2500 + text.length * 90);

export const isSpeaking = (): boolean => speaking;

const finish = (): void => {
    if (fallbackTimer) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
    }
    if (!speaking) return;
    speaking = false;

    const callback = doneCallback;
    doneCallback = null;
    if (callback) callback();
};

/*
  صدای ابری (TTS اوپن‌ای‌آی).

  یک عنصر Audio مشترک نگه داشته می‌شود و فقط src اش عوض می‌شود — این
  تنها راهی است که iOS اجازه‌ی پخش را بعد از اولین تعامل کاربر نگه
  می‌دارد. اگر هر بار عنصر تازه بسازیم، سافاری پخش دوم را بی‌صدا رد
  می‌کند.
*/
let cloudAudio: HTMLAudioElement | null = null;
let cloudUrl: string | null = null;
/** آخرین تلاش صدای ابری شکست خورد؟ برای پیام خطای درست لازم است. */
let cloudFailed = false;

/**
 * علت واقعی سکوت.
 *
 * بدون این، هر شکستی به «صدای فارسی پیدا نشد» ترجمه می‌شد و کاربر
 * می‌رفت سراغ دانلود صدا، در حالی که ایراد از خاموش بودن سرور دستیار
 * بود.
 */
export const lastSpeechFailure = (): "cloud" | "local" =>
    cloudFailed ? "cloud" : "local";

const SILENT_CLIP =
    "data:audio/mp3;base64,//MkxAAHiAICWABIGeQXBdAAAJ/0AAAAAAAAAAAAAAAAAAA=";

const releaseCloudUrl = (): void => {
    if (cloudUrl) {
        URL.revokeObjectURL(cloudUrl);
        cloudUrl = null;
    }
};

/**
 * باید *داخل* رویداد کلیک صدا زده شود.
 *
 * سافاری اجازه‌ی پخش را به عنصری می‌دهد که یک بار داخل تعامل کاربر
 * پخش شده باشد. چون صدای ابری با تأخیر شبکه می‌آید، آن لحظه دیگر
 * «داخل کلیک» نیست — پس همان اول یک کلیپ بی‌صدا پخش می‌کنیم تا قفل
 * باز شود.
 */
export const unlockCloudAudio = (): void => {
    try {
        if (!cloudAudio) cloudAudio = new Audio();
        cloudAudio.muted = true;
        cloudAudio.src = SILENT_CLIP;
        const played = cloudAudio.play();
        if (played && played.catch) played.catch(() => undefined);
    } catch {
        /* نادیده — بدترین حالت، صدای اول پخش نمی‌شود */
    }
};

export const stopSpeaking = (): void => {
    try {
        window.speechSynthesis.cancel();
    } catch {
        /* نادیده */
    }
    try {
        if (cloudAudio) {
            cloudAudio.pause();
            cloudAudio.removeAttribute("src");
        }
    } catch {
        /* نادیده */
    }
    releaseCloudUrl();
    piperStop();
    finish();
};

/**
 * @param onDone بعد از تمام شدن خواندن صدا زده می‌شود — یک بار، چه
 *               عادی تمام شود چه نصفه بماند. اگر خوانده نشد (صدا
 *               خاموش است یا متن خالی)، همان لحظه صدا زده می‌شود.
 */
export const speak = (
    text: string,
    onDone?: () => void,
    onError?: (reason: string) => void
): void => {
    if (!enabled) {
        if (onDone) onDone();
        return;
    }

    const clean = forSpeech(text);
    if (!clean) {
        if (onDone) onDone();
        return;
    }

    // ترتیب لایه‌ها: صدای ابری (بهترین کیفیت فارسی) → Piper محلی →
    // صدای مرورگر. اگر لایه‌ی بالاتر نشد، خودش به پایینی می‌افتد.
    if (isCloudTtsEnabled()) {
        speakWithCloud(clean, onDone, onError);
        return;
    }

    speakLocally(clean, onDone, onError);
};

/**
 * پخش از فایل کامل — راه پیش‌فرض، چون مطمئن است.
 *
 * کل mp3 گرفته می‌شود، بعد پخش. حدود یک ثانیه از پخش جریانی کندتر
 * است، ولی هیچ‌وقت شکست نمی‌خورد.
 *
 * تاریخچه‌ی این تصمیم مهم است: نسخه‌ی جریانی ساخته شد تا صدا زودتر
 * شروع شود، و دو بار پشت سر هم صدا را روی کروم از کار انداخت
 * («مرورگر نتوانست صدا را پخش کند») — چون پاسخ chunked بدون
 * Content-Length و بدون پشتیبانی Range را مرورگر همیشه باز نمی‌کند.
 * یک ثانیه سود، به قیمت بی‌صدا شدن دستیار نمی‌ارزید. پخش جریانی
 * هنوز در کد هست ولی فقط با REACT_APP_ASSISTANT_TTS_STREAM=1 روشن
 * می‌شود.
 */
const playAsBlob = (
    clean: string,
    onDone?: () => void,
    onError?: (reason: string) => void
): void => {
    fetchSpeech(clean).then((blob) => {
        if (!blob || !cloudAudio) {
            cloudFailed = true;
            speaking = false;
            doneCallback = null;
            if (fallbackTimer) {
                clearTimeout(fallbackTimer);
                fallbackTimer = null;
            }
            speakLocally(clean, onDone, onError);
            return;
        }

        try {
            releaseCloudUrl();
            cloudUrl = URL.createObjectURL(blob);

            let played = false;
            cloudAudio.onplay = () => {
                played = true;
                cloudFailed = false;

                /*
                  مهلت را به اندازه‌ی *خودِ صدا* تنظیم می‌کنیم.

                  بدون این خط، مهلت ۱۲ ثانیه‌ایِ «شروع شود» سر جایش
                  می‌ماند و برای هر جواب بلندتر از ۱۲ ثانیه زودتر از
                  موعد finish() را صدا می‌زد. در گفت‌وگوی پیوسته یعنی
                  میکروفون *وسط حرف زدن دستیار* باز می‌شد، دستیار صدای
                  خودش را می‌شنید و تشخیص گفتار به هم می‌ریخت — همان
                  «دقت گوش دادن پایین آمده».
                */
                if (fallbackTimer) clearTimeout(fallbackTimer);
                const seconds =
                    cloudAudio && isFinite(cloudAudio.duration) ? cloudAudio.duration : 0;
                fallbackTimer = setTimeout(
                    finish,
                    seconds > 0 ? seconds * 1000 + 2000 : speechTimeoutFor(clean)
                );
            };
            cloudAudio.onended = () => {
                releaseCloudUrl();
                finish();
            };
            cloudAudio.onerror = () => {
                releaseCloudUrl();
                if (!played && onError) onError("audio-decode");
                finish();
            };
            cloudAudio.src = cloudUrl;

            /*
              load() بعد از عوض کردن src لازم است.

              عنصری که یک بار خطا داده، تا load() صدا زده نشود در حالت
              خطا می‌ماند و src تازه را هم پخش نمی‌کند. چون این عنصر
              عمداً بازاستفاده می‌شود (قفل سافاری)، بدون این خط اولین
              خطا دستیار را برای همیشه بی‌صدا می‌کرد.
            */
            if (typeof cloudAudio.load === "function") cloudAudio.load();

            const promise = cloudAudio.play();
            if (promise && promise.catch) {
                promise.catch(() => {
                    if (onError) onError("autoplay-blocked");
                    finish();
                });
            }
        } catch {
            finish();
        }
    });
};

/** صدای اوپن‌ای‌آی؛ اگر نشد، به لایه‌های محلی می‌افتد. */
const speakWithCloud = (
    clean: string,
    onDone?: () => void,
    onError?: (reason: string) => void
): void => {
    finish();
    speaking = true;
    doneCallback = onDone || null;

    /*
      دو مهلت جدا، نه یکی.

      قبلاً یک مهلتِ «ساخت صدا + پخش» بود که برای یک جواب بلند به ۲۸
      ثانیه می‌رسید. در گفت‌وگوی پیوسته، میکروفون تا پایان همین مهلت
      بسته می‌ماند — پس اگر پخش هیچ‌وقت شروع نمی‌شد (مسدود شدن
      autoplay، شکست decode)، کاربر نیم‌دقیقه حرف می‌زد و دستیار
      «نمی‌شنید».

      حالا: ۱۲ ثانیه برای «شروع شود»، و بعد از شروع، مهلتی به اندازه‌ی
      خودِ صدا.
    */
    fallbackTimer = setTimeout(finish, 12000);

    if (!cloudAudio) {
        try {
            cloudAudio = new Audio();
        } catch {
            /* نادیده — پایین به لایه‌ی محلی می‌افتد */
        }
    }

    /*
      پیش‌فرض: فایل کامل (مطمئن). پخش جریانی فقط با فلگ صریح.

      جریانی حدود یک ثانیه زودتر صدا را شروع می‌کند، ولی دو بار روی
      کروم صدا را کامل از کار انداخت. پس تا وقتی با MediaSource درست
      پیاده نشود، پیش‌فرض نیست.
    */
    const streaming = String(process.env.REACT_APP_ASSISTANT_TTS_STREAM || "").trim() === "1";
    if (!streaming) {
        playAsBlob(clean, onDone, onError);
        return;
    }

    const url = speechUrl(clean);
    if (!url) {
        cloudFailed = true;
        speaking = false;
        doneCallback = null;
        if (fallbackTimer) {
            clearTimeout(fallbackTimer);
            fallbackTimer = null;
        }
        speakLocally(clean, onDone, onError);
        return;
    }

    const toLocal = () => {
        // سرور صدا در دسترس نبود — به لایه‌ی محلی می‌افتیم، ولی علت
        // واقعی را نگه می‌داریم: اگر لایه‌ی محلی هم نشد، پیام «صدای
        // فارسی نیست» گمراه‌کننده است. ایراد از سرور بوده.
        cloudFailed = true;
        speaking = false;
        doneCallback = null;
        if (fallbackTimer) {
            clearTimeout(fallbackTimer);
            fallbackTimer = null;
        }
        speakLocally(clean, onDone, onError);
    };

    {
        try {
            if (!cloudAudio) cloudAudio = new Audio();
            releaseCloudUrl();

            cloudAudio.muted = false;
            let started = false;

            // پخش واقعاً شروع شد → مهلت را به اندازه‌ی خود صدا می‌کنیم
            cloudAudio.onplay = () => {
                started = true;
                cloudFailed = false;
                if (fallbackTimer) clearTimeout(fallbackTimer);
                const seconds = cloudAudio && isFinite(cloudAudio.duration)
                    ? cloudAudio.duration
                    : 0;
                fallbackTimer = setTimeout(
                    finish,
                    seconds > 0 ? seconds * 1000 + 2000 : speechTimeoutFor(clean)
                );
            };
            cloudAudio.onended = finish;
            cloudAudio.onerror = () => {
                /*
                  اگر وسط پخش بود، همان‌جا تمامش می‌کنیم.

                  ولی اگر هنوز یک بایت هم پخش نشده، یعنی مرورگر نتوانست
                  پاسخ جریانی (chunked، بدون Content-Length و بدون
                  پشتیبانی Range) را باز کند. این روی کروم واقعاً اتفاق
                  افتاد و پیام «مرورگر نتوانست صدا را پخش کند» می‌داد.
                  در آن حالت همان فایل را یک‌جا می‌گیریم و پخش می‌کنیم —
                  کمی کندتر، ولی مطمئن.
                */
                if (started) finish();
                else playAsBlob(clean, onDone, onError);
            };
            cloudAudio.src = url;

            const played = cloudAudio.play();
            if (played && played.catch) {
                played.catch(() => {
                    // سافاری بدون قفل‌شکنی اجازه نمی‌دهد
                    if (onError) onError("autoplay-blocked");
                    finish();
                });
            }
        } catch {
            toLocal();
        }
    }
};

/**
 * صدای محلی Piper.
 *
 * یک بار خاموش شده بود چون مدل و wasm را از CDN می‌گرفت و خطاهای
 * مدیریت‌نشده‌ی خودِ کتابخانه روی صفحه ظاهر می‌شد. حالا همه‌ی آن
 * فایل‌ها از public/piper/ (سرور خودمان) می‌آیند، پس دوباره روشن است
 * و با `REACT_APP_ASSISTANT_PIPER=0` می‌شود خاموشش کرد.
 */
const isPiperEnabled = (): boolean =>
    String(process.env.REACT_APP_ASSISTANT_PIPER || "").trim() !== "0";

/** لایه‌های محلی: Piper اگر فعال و آماده باشد، وگرنه صدای مرورگر. */
const speakLocally = (
    clean: string,
    onDone?: () => void,
    onError?: (reason: string) => void
): void => {
    if (!pickVoice() && isPiperEnabled() && isPiperReady()) {
        finish();
        speaking = true;
        doneCallback = onDone || null;
        fallbackTimer = setTimeout(finish, speechTimeoutFor(clean));

        speakWithPiper(clean, finish).then((spoke) => {
            if (spoke) return;
            speaking = false;
            doneCallback = null;
            if (fallbackTimer) {
                clearTimeout(fallbackTimer);
                fallbackTimer = null;
            }
            speakWithBrowser(clean, onDone, onError);
        });
        return;
    }

    speakWithBrowser(clean, onDone, onError);
};

/** خواندن با صدای خود مرورگر (speechSynthesis). */
const speakWithBrowser = (
    clean: string,
    onDone?: () => void,
    onError?: (reason: string) => void
): void => {
    if (!isSpeechSupported()) {
        if (onError) onError("unsupported");
        if (onDone) onDone();
        return;
    }

    const voice = pickVoice();
    if (!voice) {
        if (onError) onError("no-persian-voice");
        if (onDone) onDone();
        return;
    }

    try {
        // جمله‌ی قبلی باید قطع شود، وگرنه صف می‌شوند و روی هم می‌افتند.
        // finish() قبلش می‌آید تا onDone جمله‌ی قبلی جا نماند.
        finish();
        window.speechSynthesis.cancel();

        const utterance = new SpeechSynthesisUtterance(clean);
        utterance.voice = voice;
        utterance.lang = "fa-IR";
        utterance.rate = 0.96; // کمی آرام‌تر از سرعت پیش‌فرض، بدون کش‌دادن واژه‌ها
        utterance.pitch = 1;
        utterance.volume = 1;

        speaking = true;
        doneCallback = onDone || null;

        utterance.onend = finish;
        utterance.onerror = (event: SpeechSynthesisErrorEvent) => {
            if (onError) onError(event.error || "speech-failed");
            finish();
        };
        fallbackTimer = setTimeout(finish, speechTimeoutFor(clean));

        window.speechSynthesis.speak(utterance);
    } catch {
        // خوانده نشدن نباید جلوی نمایش متن را بگیرد
        if (onError) onError("speech-failed");
        finish();
        if (onDone) onDone();
    }
};
